import { keyframes, styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';

const blue = '#0d4efe';
const teal = '#01ebb2';
const ink = '#1a1a2e';
const muted = '#5a6478';

const waitlistSuccessIn = keyframes`
  0% {
    opacity: 0;
    transform: translateY(10px) scale(0.92);
    box-shadow: 0 0 0 rgba(13, 78, 254, 0);
  }
  55% {
    opacity: 1;
    transform: translateY(-2px) scale(1.02);
    box-shadow: 0 6px 10px rgba(13, 78, 254, 0.16), 0 16px 36px rgba(1, 201, 160, 0.28);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
    box-shadow: 0 4px 6px rgba(13, 78, 254, 0.12), 0 12px 28px rgba(13, 78, 254, 0.22);
  }
`;

export const WaitlistCard = styled(Box)(({ theme }) => ({
  width: '100%',
  margin: '0 0 4px',
  padding: '16px 18px 14px',
  borderRadius: 16,
  background: '#fff',
  border: '1px solid rgba(13, 78, 254, 0.12)',
  boxShadow: '0 8px 28px rgba(13, 78, 254, 0.08)',
  textAlign: 'left',
  fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
  [theme.breakpoints.up('sm')]: {
    padding: '18px 22px 14px',
  },
}));

export const WaitlistHeadline = styled('h2')(() => ({
  margin: '0 0 4px',
  color: ink,
  fontSize: '1.15rem',
  fontWeight: 700,
  letterSpacing: '-0.02em',
  lineHeight: 1.25,
}));

export const WaitlistSubcopy = styled('p')(() => ({
  margin: '0 0 12px',
  color: muted,
  fontSize: '0.875rem',
  lineHeight: 1.4,
}));

export const WaitlistFields = styled(Box)(({ theme }) => ({
  display: 'grid',
  gap: theme.spacing(1),
  gridTemplateColumns: '1fr',
  '& .MuiFormHelperText-root': {
    marginTop: 2,
    marginLeft: 2,
    lineHeight: 1.2,
  },
  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: '1fr 1fr',
    columnGap: theme.spacing(2),
  },
}));

export const WaitlistFullRow = styled(Box)(({ theme }) => ({
  minWidth: 0,
  [theme.breakpoints.up('sm')]: {
    gridColumn: '1 / -1',
  },
  '& > *': {
    marginTop: 0,
    marginBottom: 0,
  },
}));

export const WaitlistContactRow = styled(WaitlistFullRow)(() => ({
  '& > *': {
    padding: '8px 12px',
  },
  '& > * > div:first-of-type': {
    marginBottom: 6,
    fontSize: '0.85rem',
  },
  '& button': {
    padding: '7px 12px',
  },
}));

export const WaitlistSubmitButton = styled(Button)(({ theme }) => ({
  justifySelf: 'start',
  textTransform: 'none',
  fontWeight: 700,
  borderRadius: 10,
  padding: '7px 22px',
  minWidth: 180,
  [theme.breakpoints.up('sm')]: {
    gridColumn: '1 / -1',
    minWidth: 220,
  },
  color: '#fff',
  background: `linear-gradient(90deg, ${teal} 0%, ${blue} 100%)`,
  boxShadow: '0 4px 14px rgba(13, 78, 254, 0.35)',
  '&:hover': {
    background: `linear-gradient(90deg, ${teal} 0%, ${blue} 100%)`,
    opacity: 0.93,
    boxShadow: '0 6px 18px rgba(13, 78, 254, 0.42)',
  },
  '&:disabled': {
    background: 'rgba(0, 0, 0, 0.12)',
    color: 'rgba(255, 255, 255, 0.7)',
    boxShadow: 'none',
  },
}));

export const WaitlistSuccess = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$animate',
})(({ $animate = true }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 12,
  padding: '14px 16px',
  borderRadius: 12,
  border: '1px solid rgba(255, 255, 255, 0.35)',
  color: '#fff',
  background: 'linear-gradient(135deg, #0B3FD4 0%, #0D4EFE 42%, #01C9A0 100%)',
  boxShadow:
    '0 4px 6px rgba(13, 78, 254, 0.12), 0 12px 28px rgba(13, 78, 254, 0.22)',
  fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
  transformOrigin: 'center top',
  ...($animate
    ? {
        animation: `${waitlistSuccessIn} 520ms cubic-bezier(0.22, 1.2, 0.36, 1) both`,
        '@media (prefers-reduced-motion: reduce)': {
          animation: 'none',
        },
      }
    : { animation: 'none' }),
}));

export const WaitlistSuccessIconWrap = styled('span')(() => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  marginTop: 1,
  color: '#fff',
  '& .MuiSvgIcon-root': {
    fontSize: 22,
  },
}));

export const WaitlistSuccessBody = styled(Box)(() => ({
  minWidth: 0,
  flex: 1,
}));

export const WaitlistSuccessTitle = styled('h2')(() => ({
  margin: '0 0 4px',
  color: '#fff',
  fontSize: '1.05rem',
  fontWeight: 700,
  letterSpacing: '-0.01em',
  lineHeight: 1.3,
}));

export const WaitlistSuccessText = styled('p')(() => ({
  margin: 0,
  color: 'rgba(255, 255, 255, 0.92)',
  fontSize: '0.95rem',
  fontWeight: 500,
  lineHeight: 1.45,
}));

export const PreferenceError = styled('p')(() => ({
  margin: '8px 0 0',
  textAlign: 'left',
  color: '#b91c1c',
  fontSize: '0.75rem',
}));
